import { policyType as PolicyType, policyTypeNames, standaloneRentersStates } from '@ourbranch/lookups';

import homeIcon from 'core/assets/svg/ic-home-32px.svg';
import autoIcon from 'core/assets/svg/ic-auto-32px.svg';
import autoAndRentersIcon from 'core/assets/svg/ic-auto-n-renters-32px.svg';
import condoIcon from 'core/assets/svg/ic-condo-32px.svg';
import rentersIcon from 'core/assets/svg/ic-renters-32px.svg';

// Auto terms are 6 months; home, condo and renters terms are 12.
const AUTO_TERM_MONTHS = 6;
const PROPERTY_TERM_MONTHS = 12;

// Home & Auto is offered as one option; picking only one of its lines selects these "fake" types.
const optionTypeForSelection = {
  [PolicyType.HomeBundle]: PolicyType.HABundle,
  [PolicyType.AutoBundle]: PolicyType.HABundle
};

// The quote.global field names for one line. Home's payment fields carry the older "homeowners" prefix.
const fieldsFor = (key) => {
  const paymentPrefix = key === 'home' ? 'homeowners' : key;
  return {
    effectiveDate: `${key}EffectiveDate`,
    billingDayOfMonth: `${key}BillingDayOfMonth`,
    paymentMethod: `${paymentPrefix}PaymentMethod`,
    paymentType: `${paymentPrefix}PaymentType`,
    megaDownPay: `${key}MegaDownPay`
  };
};

const buildLine = ({ key, title, icon, termMonths, option, global }) => {
  const fields = fieldsFor(key);
  return {
    key,
    title,
    icon,
    termMonths,
    optionType: option.type,
    fields,
    total: option[`${key}Total`],
    schedule: option[`${key}Schedule`] ?? null,
    paymentMethod: global[fields.paymentMethod],
    paymentType: global[fields.paymentType],
    megaDownPay: Boolean(global[fields.megaDownPay]),
    effectiveDate: global[fields.effectiveDate],
    billingDayOfMonth: global[fields.billingDayOfMonth]
  };
};

/**
 * One entry per policy the selected option sells, in the order today's price details card lists them
 * (home, condo, auto, renters). Each entry carries what the policy card and its slot fills need: the
 * option's price and server-computed schedule for that line, and the line's billing settings from the
 * offer's saved quote.global.
 *
 * Renters & Auto is one policy outside standalone-renters states (renters rides on the auto policy), so
 * it's one card. In standalone-renters states renters is its own policy and gets its own card.
 */
export const getPolicyLines = ({ selectedOption, options, global, state }) => {
  const selection = selectedOption || '';
  const optionType = optionTypeForSelection[selection] ?? selection;
  const option = options?.find(({ type }) => type === optionType);
  if (!option || !global) {
    return [];
  }

  const isStandaloneRenters = Boolean(standaloneRentersStates[state]);
  const isRentersOnAuto = selection === PolicyType.ARBundle && !isStandaloneRenters;
  const lines = [];

  if (selection.includes(PolicyType.Home)) {
    lines.push(
      buildLine({
        key: 'home',
        title: policyTypeNames[PolicyType.Home],
        icon: homeIcon,
        termMonths: PROPERTY_TERM_MONTHS,
        option,
        global
      })
    );
  }
  if (selection.includes(PolicyType.Condo)) {
    lines.push(
      buildLine({
        key: 'condo',
        title: policyTypeNames[PolicyType.Condo],
        icon: condoIcon,
        termMonths: PROPERTY_TERM_MONTHS,
        option,
        global
      })
    );
  }
  // A bundle option can exist with auto unpriced (e.g. every vehicle soft-rejected).
  if (selection.includes(PolicyType.Auto) && option.autoTotal != null) {
    lines.push(
      buildLine({
        key: 'auto',
        title: policyTypeNames[isRentersOnAuto ? PolicyType.ARBundle : PolicyType.Auto],
        icon: isRentersOnAuto ? autoAndRentersIcon : autoIcon,
        termMonths: AUTO_TERM_MONTHS,
        option,
        global
      })
    );
  }
  if (selection.includes(PolicyType.Renters) && isStandaloneRenters) {
    lines.push(
      buildLine({
        key: 'renters',
        title: policyTypeNames[PolicyType.Renters],
        icon: rentersIcon,
        termMonths: PROPERTY_TERM_MONTHS,
        option,
        global
      })
    );
  }

  return lines;
};
